import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const FeedSharingCard = ({ userId }: { userId: string | null }) => {
  const { toast } = useToast();
  const [autoShare, setAutoShare] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    let cancelled = false;
    supabase.from("profiles").select("feed_opt_out_default").eq("user_id", userId).maybeSingle().then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        toast({ title: "Couldn't load sharing preference", description: "Please reload to try again.", variant: "destructive" });
        return;
      }
      setAutoShare(!data?.feed_opt_out_default);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId, toast]);

  const change = async (next: boolean) => {
    if (!userId) return;
    setSaving(true);
    const { data, error } = await supabase.from("profiles").update({ feed_opt_out_default: !next }).eq("user_id", userId).select("feed_opt_out_default").single();
    setSaving(false);
    if (error || !data) {
      toast({ title: "Couldn't save preference", description: "Please try again.", variant: "destructive" });
      return;
    }
    setAutoShare(!data.feed_opt_out_default);
    toast({ title: next ? "New designs will be shared" : "New designs won't be shared" });
  };

  return (
    <div className="bg-card border border-border p-6 w-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-foreground font-medium">Share new designs to feed</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Only final images and furniture options are shared. No personal info is ever shared. Existing designs keep
            their current setting, and you can change any design individually.
          </p>
        </div>
        <Switch checked={autoShare} onCheckedChange={change} disabled={loading || saving} aria-label="Share new designs to feed" />
      </div>
    </div>
  );
};

export default FeedSharingCard;